import { api } from "@/modules/api";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { FC, useCallback } from "react";
import styled from "@emotion/styled";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { ImportItem } from "@/pages/settings/components/migration/import-item";
import { MDSButton } from "@/mds";
import { OptimisticDataImportItem } from "@/pages/settings/components/migration/optimistic-data-import-item";
import { MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";

export const SettingsAccountMigrationImporterSection: FC = observer(() => {
  const { store } = useAppStore();
  const latestImport = store.dataImports.latestImport;
  const importIsRunning = !!latestImport?.isRunning;

  const importAccountContentFromV1 = useCallback(async () => {
    const isConfirmed = confirm(
      "Are you sure you want to import your notes and collections from Mem 1.0?"
    );

    if (!isConfirmed) {
      return;
    }

    const result = await api.post("/v2/migration/import-account-content", {
      body: {
        config: {
          generate_unique_v2_ids: false,
        },
      },
    });

    if (result.error || !result.data) {
      alert("An error occurred.");
      return;
    }

    /**
     * Track starting an import.
     */
    trackEvent(TrackedEvent.ImportFrom1Dot0StartImport);

    alert(
      "Import started successfully! Note that this may take up to 1 hour to complete. You'll now be redirected to your notes."
    );
    setTimeout(() => {
      store.navigation.goToNotes();
    }, 1_000);
  }, [store.navigation]);

  const anyImportIsRunning = store.dataImports.getLatestImports(3).some(imp => imp.isRunning);
  const shouldShowExtraLoaderIcon = !anyImportIsRunning;

  return (
    <ContentWrapper>
      <MDSText variant={MDSTextVariant.ParagraphS}>
        Import your notes and collections created in Mem 1.0 to jumpstart your experience in Mem
        2.0.
      </MDSText>
      <SectionWrapper>
        <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Bold}>
          How it works
        </MDSText>
        <StyledList>
          <li>
            <b>Only notes and collections you created will be imported.</b> Notes and collections
            shared with you by others will not be imported.
          </li>
          <li>
            <b>Importing may take up to 1 hour.</b> The time is dependent on the number of notes and
            collections you have.
          </li>
          <li>
            <b>You cannot undo this import.</b> To remove imported notes or collections, you will
            need to manually delete the imported notes and collections. You can multi-select notes
            to delete them in bulk.
          </li>
          <li>
            <b>Content will be imported from Mem 1.0 account with the same email.</b> If you need to
            import content from another Mem 1.0 account, please contact support.
          </li>
        </StyledList>
      </SectionWrapper>
      <SectionWrapper>
        <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Bold}>
          Your imports
        </MDSText>
        {!importIsRunning && (
          <>
            <MDSButton onClick={importAccountContentFromV1}>
              Import all notes and collections from Mem 1.0
            </MDSButton>
            <br />
          </>
        )}
        <ImportsContainer>
          {shouldShowExtraLoaderIcon && <OptimisticDataImportItem />}
          {store.dataImports.getLatestImports(3).map(imp => (
            <ImportItem dataImport={imp} key={imp.id} />
          ))}
        </ImportsContainer>
      </SectionWrapper>
      {importIsRunning && (
        <MDSButton onClick={importAccountContentFromV1} isDisabled>
          Import all notes and collections from Mem 1.0
        </MDSButton>
      )}
    </ContentWrapper>
  );
});

const ImportsContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  gap: 8,
});

const StyledList = styled.ul`
  margin: 0;
  padding-left: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;
